import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAdsMetrics } from '../hooks/useAdsMetrics';
import { useCampaigns } from '../hooks/useCampaigns';
import { useAdsState } from '../hooks/useAdsState';
import { useLatestAdsAudit } from '../hooks/useOverview';
import { campaignsApi, type OverviewRange } from '../api/campaignsApi';
import type { AdCampaign } from '../api/adsApi';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { useAuthContext } from '@/shared/context/useAuthContext';
import { MetricCard } from '@/shared/ui';
import { CampaignsTable } from '../components/CampaignsTable';
import { AdsAuditCard } from '../components/AdsAuditCard';
import { AdsTrendChart } from '../components/AdsTrendChart';
import { ZeroStateOnboarding } from '../components/ZeroStateOnboarding';
import { AdsConnectionFailed } from '../components/AdsConnectionFailed';
import { RecommendationsBanner } from '../components/RecommendationsBanner';
import { StrategyNudgeBanner, StrategyActiveBanner } from '../components/StrategyNudgeBanner';
import { Search, Facebook } from 'lucide-react';
import { SyncNowButton } from '../components/SyncNowButton';
import { useTriggerAdsSync } from '../hooks/useTriggerAdsSync';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { CTABanner } from '@/shared/ui/CTABanner';
import { PageHeader } from '@/shared/ui';
import { formatConversions, formatCurrency } from '@/shared/utils/format';
import { integrationKeys } from '@/modules/settings/queryKeys';
import { campaignKeys } from '../queryKeys';
import styles from './AdsDashboardPage.module.css';

const OVERVIEW_RANGES: Array<{ value: OverviewRange; label: string }> = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: '1y', label: 'Last 12 months' },
];

/**
 * Ads v9 Overview page. Renders from the dimensional `useAdsState()`:
 *  - no connected account OR no campaigns → full-page ZeroStateOnboarding (the
 *    connect card is hidden once an account is connected)
 *  - connected, has campaigns             → audit score + metrics + trend + tables
 * Strategy banners are keyed off `adsState.strategy`; `adsState.mature` switches the >30-day copy.
 */
export function AdsDashboardPage() {
  const { currentLocation } = useLocation();
  const navigate = useNavigate();
  const adsState = useAdsState();
  const { syncNow, syncing } = useTriggerAdsSync();
  // Range lives in the URL (?range=…) so a campaign opened from here can send the
  // user back to this exact view — same range — via the detail page's Back link.
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get('range');
  const range: OverviewRange = OVERVIEW_RANGES.find((o) => o.value === rangeParam)?.value ?? '30d';
  const setRange = (r: OverviewRange) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('range', r);
        return next;
      },
      { replace: true },
    );
  };
  const rangeLabel = OVERVIEW_RANGES.find((o) => o.value === range)?.label ?? '';
  const metricsQuery = useAdsMetrics(range);
  const campaignsQuery = useCampaigns(range);
  const auditQuery = useLatestAdsAudit();
  const { impersonatedPractice } = useImpersonation();
  const { user } = useAuthContext();
  const practiceId = impersonatedPractice?.id ?? user?.practiceId ?? '';
  const locationId = currentLocation?.id ?? '';

  const connectGoogleMutation = useMutation({
    mutationFn: () => integrationsApi.googleAds.initiateConnect().then((r) => r.data),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
  });

  const { data: googleAdsStatus, isLoading: googleStatusLoading } = useQuery({
    queryKey: integrationKeys.googleAdsStatus(practiceId),
    queryFn: () => integrationsApi.googleAds.getStatus().then((r) => r.data),
    enabled: !!practiceId,
  });

  const { data: metaAdsStatus, isLoading: metaStatusLoading } = useQuery({
    queryKey: integrationKeys.metaAdsStatus(),
    queryFn: () => integrationsApi.metaAds.getStatus().then((r) => r.data),
  });

  const metrics = metricsQuery.data?.data;
  const allCampaigns = campaignsQuery.data?.data ?? [];
  // Active campaigns first, then paused, each ordered by spend.
  const sortActiveFirst = (a: AdCampaign, b: AdCampaign) => {
    const rank = (s: string) =>
      s.toLowerCase() === 'paused' || s.toLowerCase() === 'ended' ? 1 : 0;
    return rank(a.status) - rank(b.status) || b.spend - a.spend;
  };
  const googleCampaigns = allCampaigns
    .filter((c) => c.platform.toLowerCase() === 'google')
    .sort(sortActiveFirst);
  const metaCampaigns = allCampaigns
    .filter((c) => c.platform.toLowerCase() === 'meta')
    .sort(sortActiveFirst);

  const isGoogleConnected = googleAdsStatus?.connected && googleAdsStatus.status === 'connected';
  const isMetaConnected = metaAdsStatus?.connected && metaAdsStatus.status === 'connected';
  const statusLoading = googleStatusLoading || metaStatusLoading;
  const showConnectCta = !statusLoading && (!isGoogleConnected || !isMetaConnected);

  const audit = auditQuery.data?.data ?? null;
  const auditTone = adsState.strategy === 'active' ? 'strategy' : 'general';

  const marketingCampaignsQuery = useQuery({
    queryKey: campaignKeys.marketingByLocation(locationId),
    queryFn: () => campaignsApi.list(locationId),
    enabled: adsState.loaded && adsState.accounts.any,
  });
  const marketingCampaigns = marketingCampaignsQuery.data?.data ?? [];
  const recommendedCount = marketingCampaigns.filter((c) => c.status === 'recommended').length;
  const hasLivePnCampaigns = marketingCampaigns.some(
    (c) =>
      c.source !== 'external' &&
      ['live', 'pending_creative', 'approved', 'launching', 'ready_for_review'].includes(c.status),
  );

  if (!adsState.loaded) {
    return (
      <div className={styles.page}>
        <PageHeader title="Ad Campaigns" />
      </div>
    );
  }

  // Zero state: full-page onboarding when there are no campaigns to show — whether
  // no ad account is connected yet, or one is connected but hasn't produced any
  // campaigns. When connected, the connect card is dropped (nothing left to connect)
  // and only the strategy CTA remains.
  if (adsState.accounts.connectionFailed) {
    return (
      <AdsConnectionFailed
        title="Ad Campaigns"
        className={styles.page}
        failures={adsState.accounts.failed}
      />
    );
  }

  if (!adsState.accounts.any || adsState.campaigns === 'none') {
    return (
      <div className={styles.page}>
        <ZeroStateOnboarding
          onConnectGoogle={() => connectGoogleMutation.mutate()}
          googleLoading={connectGoogleMutation.isPending}
          strategy={adsState.strategy}
          campaigns={adsState.campaigns}
          accountsConnected={adsState.accounts.any}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <PageHeader
        title="Ad Campaigns"
        subtitle="Campaign health score, budget overview, and optimization signals"
        timestamp={auditQuery.data?.data?.runAt}
        actions={
          <div className={styles.headerControls}>
            <select
              className={styles.rangeSelect}
              value={range}
              onChange={(e) => setRange(e.target.value as OverviewRange)}
              aria-label="Date range"
            >
              {OVERVIEW_RANGES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <SyncNowButton syncing={syncing} onSync={syncNow} />
          </div>
        }
      />

      {/* Connect Your Accounts — show when Google Ads or Meta Ads not connected */}
      {showConnectCta && (
        <CTABanner
          title="Connect Your Accounts"
          body="Connect your ad accounts so PracticeNova can analyze spend, surface waste, and recommend budget moves tailored to your practice."
          actions={[
            ...(!isGoogleConnected
              ? [
                  {
                    label: 'Connect Google Ad Account',
                    onClick: () => connectGoogleMutation.mutate(),
                    disabled: connectGoogleMutation.isPending,
                    variant: 'primary' as const,
                  },
                ]
              : []),
          ]}
        />
      )}

      {/* Strategy banner, keyed off strategy progress:
            - not_started → "create a strategy" nudge
            - in_progress → "complete your strategy" nudge
            - active, no live campaigns yet → StrategyActiveBanner (set up recs)
            - active, mature + new recs     → RecommendationsBanner (re-engagement) */}
      {adsState.strategy === 'not_started' && <StrategyNudgeBanner />}
      {adsState.strategy === 'in_progress' && <StrategyNudgeBanner strategy="in_progress" />}
      {adsState.strategy === 'active' && !hasLivePnCampaigns && (
        <StrategyActiveBanner
          count={recommendedCount}
          onSetUp={() => navigate('/campaigns/list')}
        />
      )}
      {adsState.strategy === 'active' && adsState.mature && recommendedCount > 0 && (
        <RecommendationsBanner
          count={recommendedCount}
          onClick={() => navigate('/campaigns/list')}
        />
      )}

      {/* Ads Score card */}
      {auditQuery.isLoading ? (
        <SkeletonCard />
      ) : audit ? (
        <AdsAuditCard audit={audit} tone={auditTone} />
      ) : (
        <AdsAuditCard
          tone={auditTone}
          audit={{
            id: '',
            practiceId: '',
            locationId: currentLocation?.id ?? '',
            score: 0,
            summary: 'Analyzing your ad account\u2026 Check back shortly.',
            breakdown: null,
            runAt: '',
          }}
        />
      )}

      {/* Performance metrics */}
      <div className={styles.metricsGrid}>
        {metricsQuery.isLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : metrics ? (
          <>
            <MetricCard
              title="Total Spend"
              value={formatCurrency(metrics.totalSpend)}
              sub={rangeLabel}
            />
            <MetricCard
              title="Conversions"
              value={formatConversions(metrics.conversions)}
              sub={rangeLabel}
            />
            <MetricCard
              title="Cost Per Conversion"
              value={formatCurrency(metrics.cpa)}
              sub={rangeLabel}
            />
          </>
        ) : (
          <div className={styles.emptyMetrics}>
            Connect an ad platform to see performance metrics.
          </div>
        )}
      </div>

      {/* Trend chart — driven by the page-level range */}
      <AdsTrendChart range={range} />

      {/* Google Ads campaigns */}
      {(isGoogleConnected || googleCampaigns.length > 0) && (
        <div className={styles.section}>
          <div className={styles.platformSectionHeader}>
            <div className={styles.platformSectionTitleGroup}>
              <span className={styles.platformIcon}>
                <Search size={20} strokeWidth={1.75} aria-hidden />
              </span>
              <h2 className={styles.sectionTitle}>Google Ads</h2>
            </div>
          </div>
          <CampaignsTable
            campaigns={googleCampaigns}
            loading={campaignsQuery.isLoading}
            backLabel="Overview"
          />
        </div>
      )}

      {/* Meta Ads campaigns */}
      {(isMetaConnected || metaCampaigns.length > 0) && (
        <div className={styles.section}>
          <div className={styles.platformSectionHeader}>
            <div className={styles.platformSectionTitleGroup}>
              <span className={styles.platformIcon}>
                <Facebook size={20} strokeWidth={1.75} aria-hidden />
              </span>
              <h2 className={styles.sectionTitle}>Meta Ads</h2>
            </div>
          </div>
          <CampaignsTable
            campaigns={metaCampaigns}
            loading={campaignsQuery.isLoading}
            backLabel="Overview"
          />
        </div>
      )}

      {/* Fallback if no platforms connected but campaigns exist */}
      {!isGoogleConnected && !isMetaConnected && allCampaigns.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Campaign Performance</h2>
          <CampaignsTable
            campaigns={allCampaigns}
            loading={campaignsQuery.isLoading}
            backLabel="Overview"
          />
        </div>
      )}
    </div>
  );
}
